/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { AnalyticsReporterConfiguration } from '../configuration/AnalyticsReporterConfiguration';
import { Provider } from '../Provider';
import { createLogger, Logger, LoggerLevel, UtilFactory } from '@quickplay/foundation';
import { ConvivaAnalyticsReporterConfiguration } from '../configuration/ConvivaAnalyticsReporterConfiguration';
import { PlaybackEvent } from '../../event/PlaybackEvent';
import { ApplicationEvent } from '../../event/ApplicationEvent';
import { ConvivaAnalyticsReporter } from './model/ConvivaAnalyticsReporter';
import { AdPlaybackEvent } from '../../event/AdPlaybackEvent';
import { AdMetadata } from '../../model/AdMetadata';
import { AdBreakInfo, AdContentTimePosition, AdInfo, Player, VASTProperties } from '@quickplay/player-interface';
import { getConnectionType, toDeviceReportingData } from '../util/DataUtil';
import {
  AdAnalytics,
  Analytics,
  Constants as ConvivaConstants,
  ConvivaMetadata,
  VideoAnalytics,
} from '@convivainc/conviva-js-coresdk';
import { isLive } from '../util/AnalyticsReporterUtil';
import { AdTechnology } from '../../type/AdTechnology';

const TAG_NAME = 'FL_ANALYTICS';

export const DEFAULT_AD_ASSET_NAME = 'ad name'; // check VAST info
export const DEFAULT_AD_SYSTEM = 'NA';
export const DEFAULT_AD_IS_SLATE = 'false';
export const DEFAULT_AD_MEDIA_FILE_API_FRAMEWORK = 'NA';
export const DEFAULT_AD_FIRST_AD_SYSTEM = 'NA';
export const DEFAULT_AD_STITCHER = 'NA';
export const DEFAULT_AD_ID = 'NA';
export const DEFAULT_AD_FIRST_CREATIVE_ID = 'NA';
export const DEFAULT_AD_CREATIVE_ID = 'NA';

export class ConvivaAnalyticsReporterImpl implements ConvivaAnalyticsReporter {
  private deferred_: any;
  private static instance: ConvivaAnalyticsReporter;
  private player?: Player;
  private logger?: Logger;
  private initialized: boolean;
  private videoAnalytics: VideoAnalytics | undefined;
  private adAnalytics: AdAnalytics | undefined;
  private contentMetadata?: ConvivaMetadata;
  private adMetadata?: AdMetadata;
  private convivaConfiguration?: AnalyticsReporterConfiguration;

  private constructor() {
    this.initialized = false;
  }

  initialize(configuration: AnalyticsReporterConfiguration): Promise<void> {
    this.deferred_ = new UtilFactory.Deferred<void>();
    this.convivaConfiguration = configuration;
    this.logger = configuration.logger || createLogger(LoggerLevel.OFF, TAG_NAME);

    const convivaConfig = configuration as ConvivaAnalyticsReporterConfiguration;

    if (!convivaConfig.customerKey) {
      this.deferred_.reject();
      return this.deferred_.promise;
    }

    this.adMetadata = convivaConfig.adMetadata;

    if (convivaConfig.debug) {
      if (!convivaConfig.gatewayUrl) {
        const message = '[ANALYTICS] Conviva Gateway URL is missing. Debug session not initialized';

        this.logger.log(message);

        this.deferred_.reject(message);
        return this.deferred_.promise;
      }

      const settings = {
        [ConvivaConstants.GATEWAY_URL]: convivaConfig.gatewayUrl,
        [ConvivaConstants.LOG_LEVEL]: ConvivaConstants.LogLevel.DEBUG,
      };

      Analytics.init(convivaConfig.customerKey, convivaConfig.callbackFunctions || null, settings);
    } else {
      Analytics.init(convivaConfig.customerKey, convivaConfig.callbackFunctions || null, {});
    }

    Analytics.setDeviceMetadata(toDeviceReportingData(convivaConfig.device || {}));

    // video analytics
    this.videoAnalytics = Analytics.buildVideoAnalytics();

    // ad analytics
    this.adAnalytics = Analytics.buildAdAnalytics(this.videoAnalytics);

    this.initialized = true;

    this.deferred_.resolve();
    return this.deferred_.promise;
  }

  static getInstance(): ConvivaAnalyticsReporter {
    if (!ConvivaAnalyticsReporterImpl.instance) {
      ConvivaAnalyticsReporterImpl.instance = new ConvivaAnalyticsReporterImpl();
    }

    return ConvivaAnalyticsReporterImpl.instance;
  }

  get provider(): Provider {
    return Provider.CONVIVA;
  }

  reportEvent(eventName: string, metadata?: { [key: string]: any }, _context?: any): void {
    if (!this.isInitialized()) {
      return;
    }

    switch (eventName) {
      case ApplicationEvent.APP_BACKGROUND: {
        Analytics.reportAppBackgrounded();
        break;
      }
      case ApplicationEvent.APP_FOREGROUND: {
        Analytics.reportAppForegrounded();
        break;
      }
      case PlaybackEvent.PLAYBACK_REQUEST: {
        this.contentMetadata = {
          ...metadata,
          ...(this.convivaConfiguration && {
            [ConvivaConstants.APPLICATION_VERSION]: this.convivaConfiguration?.application.version,
            [ConvivaConstants.PLAYER_NAME]: this.convivaConfiguration?.application.name,
          }),
        };

        this.videoAnalytics?.reportPlaybackRequested(this.contentMetadata);
        Analytics.reportDeviceMetric(ConvivaConstants.Network.CONNECTION_TYPE, getConnectionType());
        break;
      }
      case PlaybackEvent.PLAYBACK_END: {
        this.videoAnalytics?.reportPlaybackEnded();
        break;
      }
      case PlaybackEvent.PLAYBACK_METRIC: {
        this.videoAnalytics?.reportPlaybackMetric(metadata?.key, metadata?.value);
        break;
      }
      case PlaybackEvent.PLAYBACK_ERROR: {
        this.videoAnalytics?.reportPlaybackFailed(metadata?.errorMessage, metadata?.contentInfo);
        break;
      }
      case AdPlaybackEvent.AD_BREAK_START: {
        if (this.adMetadata && this.adMetadata.adTechnology === AdTechnology.SERVER_SIDE) {
          this.videoAnalytics?.reportAdBreakStarted(
            ConvivaConstants.AdType.SERVER_SIDE,
            ConvivaConstants.AdPlayer.CONTENT,
          );
        } else {
          this.videoAnalytics?.reportAdBreakStarted(
            ConvivaConstants.AdType.CLIENT_SIDE,
            ConvivaConstants.AdPlayer.SEPARATE,
          );
        }

        break;
      }
      case AdPlaybackEvent.AD_BREAK_END: {
        this.videoAnalytics?.reportAdBreakEnded();
        break;
      }
      case AdPlaybackEvent.AD_START: {
        this.adAnalytics?.setAdPlayerInfo(this.getPlayerInfo(this.player));
        this.adAnalytics?.reportAdStarted(this.buildAdMetadata(metadata as AdInfo));

        if (metadata?.vastProperties && metadata?.vastProperties.bitrate) {
          this.adAnalytics?.reportAdMetric(ConvivaConstants.Playback.BITRATE, metadata.vastProperties.bitrate);
        }

        break;
      }
      case AdPlaybackEvent.AD_END: {
        this.adAnalytics?.reportAdEnded();
        break;
      }
      case AdPlaybackEvent.AD_PLAYBACK_METRIC: {
        this.adAnalytics?.reportAdMetric(metadata?.key, metadata?.value);
        break;
      }
      case AdPlaybackEvent.AD_ERROR: {
        this.adAnalytics?.reportAdFailed(metadata?.errorMessage, this.buildAdMetadata());
        break;
      }
      default: {
        this.videoAnalytics?.reportPlaybackEvent(eventName, metadata);
      }
    }
  }

  reportMetadata(metadata: { [key: string]: any }, _context?: any): void {
    if (!this.isInitialized()) {
      return;
    }

    this.contentMetadata = { ...this.contentMetadata, ...metadata };
    this.videoAnalytics?.setContentInfo(metadata);
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  reset(): void {
    this.adAnalytics?.release();
    this.videoAnalytics?.release();
    Analytics.release();

    this.initialized = false;
  }

  attachPlayer(player: Player): void {
    this.player = player;

    this.videoAnalytics?.setPlayer(player.rawPlayer);

    this.videoAnalytics?.setPlayerInfo(this.getPlayerInfo(player));
  }

  /**
   * Builds player information based on the context.
   * @returns key value pair.
   */
  private getPlayerInfo = (player?: Player): { [key: string]: any } => {
    return {
      [ConvivaConstants.FRAMEWORK_NAME]: player?.internalPlayerName || '',
      [ConvivaConstants.FRAMEWORK_VERSION]: player?.internalPlayerVersion || '',
    };
  };

  /**
   * Builds ad metadata object from ad info.
   * @param adInfo
   */
  private buildAdMetadata = (adInfo?: AdInfo): ConvivaMetadata => {
    const adMetadata: ConvivaMetadata = {};

    if (this.adMetadata) {
      adMetadata['c3.ad.adStitcher'] = this.adMetadata.adStitcher ?? DEFAULT_AD_STITCHER;
      adMetadata['c3.ad.mediaFileApiFramework'] =
        this.adMetadata.adMediaFileApiFramework ?? DEFAULT_AD_MEDIA_FILE_API_FRAMEWORK;
      adMetadata['c3.ad.isSlate'] = this.adMetadata.isSlate ?? DEFAULT_AD_IS_SLATE;
      adMetadata['c3.ad.technology'] =
        this.adMetadata.adTechnology && this.adMetadata.adTechnology === AdTechnology.SERVER_SIDE
          ? ConvivaConstants.AdType.SERVER_SIDE
          : ConvivaConstants.AdType.CLIENT_SIDE;
    }

    if (adInfo) {
      this.setVastProperties(adMetadata, adInfo.vastProperties);
      adMetadata[ConvivaConstants.IS_LIVE] =
        this.contentMetadata && this.contentMetadata[ConvivaConstants.IS_LIVE]
          ? this.contentMetadata[ConvivaConstants.IS_LIVE]
          : isLive(this.player);
      adMetadata[ConvivaConstants.DURATION] = adInfo.durationMs ? adInfo.durationMs / 1000 : 0;
      adMetadata['c3.ad.id'] = adInfo.adID;
      adMetadata['c3.ad.position'] = this.toC3AdPosition(adInfo.adBreakInfo);
      adMetadata['c3.ad.firstAdSystem'] = this.toC3AdFirstAdSystem(adInfo);
      adMetadata['c3.ad.firstAdId'] = this.toC3AdFirstAdId(adInfo);
      adMetadata['c3.ad.firstCreativeId'] = this.toC3AdFirstCreativeId(adInfo);
    }

    this.setAdStreamUrl(adMetadata, adInfo);
    return adMetadata;
  };

  private setVastProperties(adMetadata: ConvivaMetadata, vastProperties?: VASTProperties): void {
    if (vastProperties) {
      adMetadata[ConvivaConstants.ASSET_NAME] = vastProperties.adTitle ?? DEFAULT_AD_ASSET_NAME;
      adMetadata['c3.ad.system'] = vastProperties.adSystem ?? DEFAULT_AD_SYSTEM;
      adMetadata['c3.ad.creativeId'] = vastProperties.creativeId ?? DEFAULT_AD_CREATIVE_ID;
    }
  }

  private setAdStreamUrl(adMetadata: ConvivaMetadata, adInfo?: AdInfo): void {
    if (adInfo && adInfo.mediaUrl) {
      adMetadata[ConvivaConstants.STREAM_URL] = adInfo.mediaUrl;
    } else if (this.adMetadata && this.adMetadata.adMediaUrl) {
      adMetadata[ConvivaConstants.STREAM_URL] = this.adMetadata.adMediaUrl;
    } else if (
      this.contentMetadata &&
      this.contentMetadata[ConvivaConstants.STREAM_URL] &&
      this.adMetadata?.adTechnology === AdTechnology.SERVER_SIDE
    ) {
      adMetadata[ConvivaConstants.STREAM_URL] = this.contentMetadata[ConvivaConstants.STREAM_URL];
    }
  }

  private toC3AdFirstAdSystem(adInfo: AdInfo): string {
    if (adInfo.vastProperties && adInfo.vastProperties.adSystem && adInfo.sequence === 1) {
      return adInfo.vastProperties.adSystem;
    }
    return DEFAULT_AD_FIRST_AD_SYSTEM;
  }

  private toC3AdFirstCreativeId(adInfo: AdInfo): string {
    if (adInfo.vastProperties && adInfo.vastProperties.creativeId && adInfo.sequence === 1) {
      return adInfo.vastProperties.creativeId;
    }
    return DEFAULT_AD_FIRST_CREATIVE_ID;
  }

  private toC3AdFirstAdId(adInfo: AdInfo): string {
    if (adInfo.sequence === 1) {
      return adInfo.adID;
    }
    return DEFAULT_AD_ID;
  }
  /**
   * Translates from FL ad break time position value to Conviva ad break
   * time position.
   * @param adBreakInfo
   */
  private toC3AdPosition = (adBreakInfo?: AdBreakInfo): any => {
    if (!adBreakInfo) {
      return ConvivaConstants.AdPosition.PREROLL;
    }

    switch (adBreakInfo.contentTimePosition) {
      case AdContentTimePosition.MIDROLL:
        return ConvivaConstants.AdPosition.MIDROLL;
      case AdContentTimePosition.POSTROLL:
        return ConvivaConstants.AdPosition.POSTROLL;
      case AdContentTimePosition.PREROLL:
      default:
        return ConvivaConstants.AdPosition.PREROLL;
    }
  };
}
